import React from "react";
import { FaTimes, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";

/**
 * Modal de confirmación reutilizable
 * @param {boolean} mostrar - Controla la visibilidad del modal
 * @param {function} onConfirmar - Callback cuando se confirma
 * @param {function} onCancelar - Callback cuando se cancela
 * @param {string} titulo - Título del modal
 * @param {string} mensaje - Mensaje principal
 * @param {string} textoConfirmar - Texto del botón de confirmar (default: "Confirmar")
 * @param {string} textoCancelar - Texto del botón de cancelar (default: "Cancelar")
 * @param {string} tipo - Tipo de confirmación: "warning" | "danger" | "info" (default: "warning")
 * @param {boolean} procesando - Deshabilita el botón de confirmar mientras se espera la respuesta del backend (evita doble clic/doble submit)
 */
const ModalConfirmacion = ({
  mostrar,
  onConfirmar,
  onCancelar,
  titulo = "Confirmación",
  mensaje,
  textoConfirmar = "Confirmar",
  textoCancelar = "Cancelar",
  tipo = "warning",
  procesando = false
}) => {
  if (!mostrar) return null;

  // Configuración de estilos según tipo
  const config = {
    warning: {
      icon: FaExclamationTriangle,
      iconColor: "text-yellow-500",
      iconBg: "bg-yellow-100",
      btnColor: "bg-yellow-500 hover:bg-yellow-600",
      borderColor: "border-yellow-200"
    },
    danger: {
      icon: FaExclamationTriangle,
      iconColor: "text-red-500",
      iconBg: "bg-red-100",
      btnColor: "bg-red-500 hover:bg-red-600",
      borderColor: "border-red-200"
    },
    info: {
      icon: FaCheckCircle,
      iconColor: "text-blue-500",
      iconBg: "bg-blue-100",
      btnColor: "bg-blue-500 hover:bg-blue-600",
      borderColor: "border-blue-200"
    }
  };

  const estilos = config[tipo] || config.warning;
  const IconoTipo = estilos.icon;

  const handleClicFuera = (e) => {
    if (e.target === e.currentTarget) {
      onCancelar();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
      style={{ zIndex: 9999 }}
      onClick={handleClicFuera}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl relative">
        {/* Botón cerrar */}
        <button
          onClick={onCancelar}
          className="absolute top-4 right-4 text-gray-400 hover:text-white hover:bg-red-500 rounded-full p-2 transition-all duration-200"
          aria-label="Cerrar"
        >
          <FaTimes className="w-4 h-4" />
        </button>

        {/* Contenido */}
        <div className="p-6">
          {/* Icono */}
          <div className="flex justify-center mb-4">
            <div className={`${estilos.iconBg} rounded-full p-3`}>
              <IconoTipo className={`w-8 h-8 ${estilos.iconColor}`} />
            </div>
          </div>

          {/* Título */}
          <h3 className="text-xl font-semibold text-gray-800 text-center mb-2">
            {titulo}
          </h3>

          {/* Mensaje */}
          <p className="text-gray-600 text-center mb-6 whitespace-pre-line">
            {mensaje}
          </p>

          {/* Botones */}
          <div className="flex gap-3">
            <button
              onClick={onCancelar}
              disabled={procesando}
              className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {textoCancelar}
            </button>
            <button
              onClick={onConfirmar}
              disabled={procesando}
              className={`flex-1 px-4 py-2.5 ${estilos.btnColor} text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {procesando ? 'Procesando...' : textoConfirmar}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmacion;

